import {
  Icon
} from "./chunk-7IEFGXFA.js";

// src/components/canvas/StatusBar.tsx
import { memo, useEffect, useRef } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
function useFpsRef(enabled) {
  const spanRef = useRef(null);
  const frames = useRef(0);
  const lastTime = useRef(performance.now());
  const rafId = useRef(0);
  const fpsRef = useRef(0);
  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      frames.current++;
      const now = performance.now();
      if (now - lastTime.current >= 1e3) {
        fpsRef.current = frames.current;
        frames.current = 0;
        lastTime.current = now;
        if (spanRef.current) {
          const fps = fpsRef.current;
          spanRef.current.textContent = `${fps} fps`;
          spanRef.current.className = `tabular-nums ${fps < 30 ? "text-destructive" : fps < 50 ? "text-amber-500" : "text-emerald-500"}`;
        }
      }
      rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId.current);
  }, [enabled]);
  return spanRef;
}
var StatusBar = memo(function StatusBar2({
  leading,
  center,
  credits,
  details,
  canvasControls = true,
  zoom,
  nodeCount,
  itemLabel = "node",
  selectedCount,
  saveState,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onShowShortcuts,
  allowWrap = false
}) {
  const zoomPercent = Math.round(zoom * 100);
  const fpsSpanRef = useFpsRef(canvasControls);
  return /* @__PURE__ */ jsxs("footer", { "aria-label": "Workspace footer", className: `fixed bottom-0 left-0 right-0 ${allowWrap ? "min-h-6 h-auto py-0.5" : "h-6"} bg-popover/80 backdrop-blur-sm border-t border-border text-xs text-muted-foreground grid items-center px-3 gap-1 z-10 pointer-events-auto select-none ${center ? "grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]" : "grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"}`, children: [
    /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-1 whitespace-nowrap", children: [
      leading && /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center", children: leading }),
      /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-1 overflow-hidden", children: [
        canvasControls && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: onZoomOut,
              className: "p-0.5 rounded hover:bg-accent transition-colors",
              "aria-label": "Zoom out",
              children: /* @__PURE__ */ jsx(Icon, { name: "minus", className: "size-3" })
            }
          ),
          /* @__PURE__ */ jsxs(
            "button",
            {
              onClick: onZoomReset,
              className: "px-1.5 py-0.5 rounded hover:bg-accent transition-colors tabular-nums min-w-[3rem] text-center",
              title: "Reset zoom to 100%",
              children: [
                zoomPercent,
                "%"
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: onZoomIn,
              className: "p-0.5 rounded hover:bg-accent transition-colors",
              "aria-label": "Zoom in",
              children: /* @__PURE__ */ jsx(Icon, { name: "plus", className: "size-3" })
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "w-px h-3 bg-border mx-1.5" }),
          /* @__PURE__ */ jsx("span", { ref: fpsSpanRef, className: "tabular-nums text-emerald-500", children: "0 fps" }),
          /* @__PURE__ */ jsx("div", { className: "w-px h-3 bg-border mx-1.5" })
        ] }),
        /* @__PURE__ */ jsxs("span", { className: "tabular-nums", children: [
          nodeCount,
          " ",
          itemLabel,
          nodeCount !== 1 ? "s" : ""
        ] }),
        selectedCount > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("span", { className: "text-muted-foreground/50 mx-0.5", children: "\xB7" }),
          /* @__PURE__ */ jsxs("span", { className: "tabular-nums", children: [
            selectedCount,
            " selected"
          ] })
        ] }),
        credits && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("div", { className: "w-px h-3 bg-border mx-1.5 shrink-0" }),
          credits
        ] })
      ] })
    ] }),
    center && /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center", children: center }),
    /* @__PURE__ */ jsxs("div", { className: `flex min-w-0 items-center justify-end gap-1 ${allowWrap ? "flex-wrap" : "overflow-hidden whitespace-nowrap"}`, children: [
      details,
      saveState.isSaving && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
        /* @__PURE__ */ jsx(Icon, { name: "loader-2", className: "size-3 animate-spin" }),
        "Saving"
      ] }),
      saveState.error && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1 text-destructive", children: [
        /* @__PURE__ */ jsx(Icon, { name: "alert-circle", className: "size-3" }),
        "Save error"
      ] }),
      !saveState.isSaving && !saveState.error && saveState.lastSaved && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
        /* @__PURE__ */ jsx(Icon, { name: "check", className: "size-3 text-green-500" }),
        "Saved"
      ] }),
      saveState.pendingChanges && !saveState.isSaving && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
        /* @__PURE__ */ jsx("span", { className: "size-1.5 rounded-full bg-amber-500" }),
        "Unsaved"
      ] }),
      /* @__PURE__ */ jsx("div", { className: "w-px h-3 bg-border mx-1.5" }),
      /* @__PURE__ */ jsxs(
        "button",
        {
          onClick: onShowShortcuts,
          className: "flex items-center gap-1 px-1 py-0.5 rounded hover:bg-accent transition-colors",
          children: [
            /* @__PURE__ */ jsx(Icon, { name: "keyboard", className: "size-3" }),
            /* @__PURE__ */ jsx("span", { children: "\u2318/" })
          ]
        }
      )
    ] })
  ] });
});

export {
  StatusBar
};
//# sourceMappingURL=chunk-VQMA2E67.js.map